/*
 * Accordion / FAQ toggle (original click actions a-53 / a-54).
 *
 * Each item toggles independently: the .accordion-item-content height animates
 * 0 <-> auto (0.3s open / 0.2s close) and the .accordion-plus-icon rotates
 * 0 <-> 135deg (0.5s), turning the plus into a cross. Items start collapsed via
 * CSS (.accordion-item-content { height: 0; overflow: hidden }).
 */
import type { gsap as GSAP } from 'gsap'

import { gsap, EASE } from './core'

export const initAccordion = (ctx: GSAP.Context, scope: HTMLElement): void => {
    ctx.add(() => {
        const cleanups: Array<() => void> = []

        gsap.utils.toArray<HTMLElement>(scope.querySelectorAll('.accordion-item-trigger')).forEach((trigger) => {
            const item = trigger.closest<HTMLElement>('.accordion-item')
            const content = item?.querySelector<HTMLElement>('.accordion-item-content')
            const icon = trigger.querySelector<HTMLElement>('.accordion-plus-icon')
            if (!item || !content) return

            const onClick = () => {
                const open = item.classList.toggle('accordion-open')
                trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
                gsap.to(content, {
                    height: open ? 'auto' : 0,
                    duration: open ? 0.3 : 0.2,
                    ease: EASE
                })
                if (icon) gsap.to(icon, { rotate: open ? 135 : 0, duration: 0.5, ease: EASE })
            }

            // Keyboard support: Enter / Space toggle the item like a click.
            const onKeyDown = (event: KeyboardEvent) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onClick()
            }

            trigger.addEventListener('click', onClick)
            trigger.addEventListener('keydown', onKeyDown)
            cleanups.push(() => {
                trigger.removeEventListener('click', onClick)
                trigger.removeEventListener('keydown', onKeyDown)
            })
        })

        return () => cleanups.forEach((fn) => fn())
    })
}
